import { useEffect, useMemo, useRef, useState } from "react";
import AppHeader from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Check, Minus, Plus, Printer, Save, Upload, RotateCcw, Settings, ChevronRight, CreditCard, TrendingUp, Calendar, Handshake } from "lucide-react";
import {
    EQUIPOS,
    CONSUMIBLES,
    DEFAULTS,
    freshState,
    autoFolio,
    deepClone,
    money,
    money0,
    unit,
    N,
    PMT,
    equipoLista,
    equiposSubtotal,
    consUnitBase,
    volDiscount,
    plazoDiscount,
    selEquipos,
    selFamilias,
    comodatoCalc,
    consWithOverrides,
    findConsRaw,
} from "@/lib/unidosis";
import QuotePanel from "@/components/unidosis/QuotePanel";
import SettingsDialog from "@/components/unidosis/SettingsDialog";

const STORAGE_KEY = "mdlink_unidosis_state_v1";

function loadState() {
    try {
        const r = localStorage.getItem(STORAGE_KEY);
        if (!r) return null;
        const s = JSON.parse(r);
        if (!s.settings) s.settings = deepClone(DEFAULTS);
        s.settings.params = { ...DEFAULTS.params, ...(s.settings.params || {}) };
        if (!s.settings.tiers) s.settings.tiers = deepClone(DEFAULTS.tiers);
        if (!s.settings.plazo) s.settings.plazo = deepClone(DEFAULTS.plazo);
        if (!s.eqOverrides) s.eqOverrides = {};
        if (!s.consOverrides) s.consOverrides = {};
        return s;
    } catch (e) {
        return null;
    }
}
function saveState(s) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
    } catch (e) {
        /* ignore */
    }
}

const MODES = [
    { id: "contado", label: "Venta directa", sub: "Pago de contado", icon: CreditCard },
    { id: "financiamiento", label: "Financiamiento", sub: "Cuotas + interés", icon: TrendingUp },
    { id: "alquiler", label: "Alquiler", sub: "1 o 2 años", icon: Calendar },
    { id: "comodato", label: "Comodato", sub: "Equipo s/costo + consumo", icon: Handshake },
];

export default function Unidosis() {
    const [state, setState] = useState(() => loadState() || freshState());
    const [settingsOpen, setSettingsOpen] = useState(false);

    // persist
    useEffect(() => {
        saveState(state);
    }, [state]);

    const patch = (p) => setState((prev) => ({ ...prev, ...p }));
    const patchDeep = (key, p) => setState((prev) => ({ ...prev, [key]: { ...prev[key], ...p } }));

    // -------- Actions
    const handleReset = () => {
        if (!window.confirm("¿Limpiar la cotización actual? (Los precios y reglas se conservan)")) return;
        const keep = state.settings;
        const eqOv = state.eqOverrides;
        const consOv = state.consOverrides;
        const fresh = freshState();
        fresh.settings = keep;
        fresh.eqOverrides = eqOv;
        fresh.consOverrides = consOv;
        setState(fresh);
        toast.success("Cotización limpia");
    };
    const handleSave = () => {
        const blob = new Blob([JSON.stringify(state, null, 2)], { type: "application/json" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = (state.folio || "cotizacion") + ".json";
        a.click();
        toast.success("Cotización descargada");
    };
    const handleLoad = () => {
        const inp = document.createElement("input");
        inp.type = "file";
        inp.accept = "application/json";
        inp.onchange = () => {
            const fr = new FileReader();
            fr.onload = () => {
                try {
                    const s = JSON.parse(fr.result);
                    if (!s.settings) s.settings = deepClone(DEFAULTS);
                    s.settings.params = { ...DEFAULTS.params, ...(s.settings.params || {}) };
                    if (!s.settings.tiers) s.settings.tiers = deepClone(DEFAULTS.tiers);
                    if (!s.settings.plazo) s.settings.plazo = deepClone(DEFAULTS.plazo);
                    if (!s.eqOverrides) s.eqOverrides = {};
                    if (!s.consOverrides) s.consOverrides = {};
                    setState(s);
                    toast.success("Cotización cargada");
                } catch (e) {
                    toast.error("Archivo no válido.");
                }
            };
            fr.readAsText(inp.files[0]);
        };
        inp.click();
    };

    return (
        <div className="min-h-screen bg-slate-50/50">
            <AppHeader subtitle="Cotizador · Unidosis Opus Pac" backTo="/" />

            {/* Sub-toolbar */}
            <div className="no-print sticky top-16 z-30 bg-white/85 backdrop-blur-xl border-b border-slate-200">
                <div className="max-w-[1400px] mx-auto px-6 h-12 flex items-center gap-2 overflow-x-auto thin-scroll">
                    <ToolButton onClick={handleReset} icon={<RotateCcw className="h-4 w-4" />} testId="btn-reset">Limpiar</ToolButton>
                    <ToolButton onClick={handleSave} icon={<Save className="h-4 w-4" />} testId="btn-save">Guardar</ToolButton>
                    <ToolButton onClick={handleLoad} icon={<Upload className="h-4 w-4" />} testId="btn-load">Cargar</ToolButton>
                    <ToolButton onClick={() => { window.scrollTo({ top: 0, behavior: "smooth" }); setSettingsOpen(true); }} icon={<Settings className="h-4 w-4" />} testId="btn-settings">
                        Precios y reglas
                    </ToolButton>
                    <div className="flex-1" />
                    <label className="flex items-center gap-2 text-xs font-medium text-slate-600 pr-2" data-testid="internal-toggle-wrap">
                        <Switch
                            data-testid="internal-toggle"
                            checked={!!state.internal}
                            onCheckedChange={(v) => patch({ internal: v })}
                            className="data-[state=checked]:bg-[#C0552B]"
                        />
                        <span>Vista interna (márgenes)</span>
                    </label>
                    <ToolButton
                        primary
                        onClick={() => window.print()}
                        icon={<Printer className="h-4 w-4" />}
                        testId="btn-print"
                    >
                        Imprimir / PDF
                    </ToolButton>
                </div>
            </div>

            <main className="max-w-[1400px] mx-auto px-6 py-6 grid lg:grid-cols-12 gap-6 items-start">
                {/* Config column */}
                <div className="lg:col-span-7 space-y-5 min-w-0 no-print">
                    <Step1 state={state} patch={patch} patchDeep={patchDeep} />
                    <Step2 state={state} setState={setState} />
                    <Step3 state={state} patch={patch} patchDeep={patchDeep} />
                    <Step4 state={state} setState={setState} patch={patch} />
                </div>

                {/* Quote column */}
                <div className="lg:col-span-5 min-w-0">
                    <div className="qsticky">
                        <QuotePanel state={state} />
                    </div>
                </div>
            </main>

            <SettingsDialog
                open={settingsOpen}
                onOpenChange={setSettingsOpen}
                state={state}
                setState={setState}
            />
        </div>
    );
}

function ToolButton({ children, onClick, icon, primary, testId }) {
    return (
        <button
            data-testid={testId}
            onClick={onClick}
            className={
                "inline-flex items-center gap-1.5 px-3 h-8 rounded-md text-[12.5px] font-medium border transition-all duration-150 whitespace-nowrap " +
                (primary
                    ? "bg-[#1BA5D9] text-white border-[#1BA5D9] hover:bg-[#0E7DAB]"
                    : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50")
            }
        >
            {icon}
            {children}
        </button>
    );
}

// =================================================================
// STEP 1 — Client data
// =================================================================
function Step1({ state, patch }) {
    return (
        <Card step={1} title="Datos de la cotización" subtitle="Cliente, tipo y vigencia">
            <div className="mb-4">
                <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500 mb-2">
                    Tipo de cliente
                </div>
                <div
                    className="inline-flex bg-slate-100 rounded-md p-1 gap-1 border border-slate-200"
                    data-testid="segment-tipo"
                >
                    <button
                        data-testid="tipo-ccss"
                        aria-pressed={state.tipo === "CCSS"}
                        onClick={() => patch({ tipo: "CCSS" })}
                        className={
                            "px-4 h-8 rounded-md text-[13px] font-medium transition-all duration-150 " +
                            (state.tipo === "CCSS"
                                ? "bg-white text-[#1F5FA8] shadow-sm"
                                : "text-slate-500 hover:text-slate-700")
                        }
                    >
                        Institucional / CCSS · IVA 0%
                    </button>
                    <button
                        data-testid="tipo-priv"
                        aria-pressed={state.tipo === "PRIV"}
                        onClick={() => patch({ tipo: "PRIV" })}
                        className={
                            "px-4 h-8 rounded-md text-[13px] font-medium transition-all duration-150 " +
                            (state.tipo === "PRIV"
                                ? "bg-white text-[#1BA5D9] shadow-sm"
                                : "text-slate-500 hover:text-slate-700")
                        }
                    >
                        Privado · IVA 13%
                    </button>
                </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Cliente">
                    <Input
                        data-testid="input-cliente"
                        placeholder="Ej. Hospital Clínica Bíblica"
                        value={state.cliente}
                        onChange={(e) => patch({ cliente: e.target.value })}
                        className="h-9 rounded-md border-slate-200 focus-visible:ring-[#1BA5D9]"
                    />
                </Field>
                <Field label="Contacto">
                    <Input
                        data-testid="input-contacto"
                        placeholder="Nombre del contacto"
                        value={state.contacto}
                        onChange={(e) => patch({ contacto: e.target.value })}
                        className="h-9 rounded-md border-slate-200 focus-visible:ring-[#1BA5D9]"
                    />
                </Field>
                <Field label="N° de cotización">
                    <Input
                        data-testid="input-folio"
                        value={state.folio}
                        onChange={(e) => patch({ folio: e.target.value })}
                        className="h-9 rounded-md border-slate-200 focus-visible:ring-[#1BA5D9] font-mono"
                    />
                </Field>
                <Field label="Vigencia (días)">
                    <Input
                        data-testid="input-vigencia"
                        type="number"
                        min={1}
                        value={state.vigencia}
                        onChange={(e) => patch({ vigencia: +e.target.value || 0 })}
                        className="h-9 rounded-md border-slate-200 focus-visible:ring-[#1BA5D9] font-mono"
                    />
                </Field>
            </div>
        </Card>
    );
}

// =================================================================
// STEP 2 — Equipment picker
// =================================================================
function Step2({ state, setState }) {
    const toggle = (id) => {
        const eq = { ...state.eq };
        if (eq[id] > 0) delete eq[id];
        else eq[id] = 1;
        setState({ ...state, eq });
    };
    const setQty = (id, v) => {
        const eq = { ...state.eq };
        const n = Math.max(0, +v || 0);
        if (n === 0) delete eq[id];
        else eq[id] = n;
        setState({ ...state, eq });
    };
    return (
        <Card step={2} title="Equipos Opus Pac" subtitle="Seleccioná y definí cantidad">
            <div className="space-y-2" data-testid="equipos-list">
                {EQUIPOS.map((e) => {
                    const qty = state.eq[e.id] || 0;
                    const on = qty > 0;
                    return (
                        <div
                            key={e.id}
                            data-testid={`eq-row-${e.id}`}
                            className={
                                "flex items-center gap-3 p-3 rounded-md border transition-all duration-150 " +
                                (on ? "border-[#1BA5D9] bg-blue-50/70" : "border-slate-200 bg-white hover:border-slate-300")
                            }
                        >
                            <button
                                data-testid={`eq-toggle-${e.id}`}
                                onClick={() => toggle(e.id)}
                                className={
                                    "h-5 w-5 rounded-sm border-2 flex-none grid place-items-center transition-all duration-150 " +
                                    (on ? "bg-[#1BA5D9] border-[#1BA5D9]" : "bg-white border-slate-300")
                                }
                                aria-label="Seleccionar equipo"
                            >
                                {on && <Check className="h-3 w-3 text-white" />}
                            </button>
                            <button
                                onClick={() => toggle(e.id)}
                                className="flex-1 min-w-0 text-left"
                                data-testid={`eq-name-${e.id}`}
                            >
                                <div className="text-[13.5px] font-semibold text-slate-900 flex items-center gap-2">
                                    {e.nombre}
                                    {e.tipo === "servicio" && (
                                        <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-800">
                                            Servicio
                                        </span>
                                    )}
                                </div>
                                <div className="text-[11.5px] text-slate-500 truncate">{e.desc}</div>
                            </button>
                            <div className="text-right whitespace-nowrap" data-testid={`eq-price-${e.id}`}>
                                <div className="font-mono font-semibold text-[13px] text-slate-900">{money0(equipoLista(state, e))}</div>
                                <div className="text-[10.5px] uppercase text-slate-400 font-mono tracking-widest">precio final</div>
                            </div>
                            {on && (
                                <div className="flex items-center border border-slate-200 rounded-md overflow-hidden flex-none">
                                    <button
                                        data-testid={`eq-dec-${e.id}`}
                                        onClick={() => setQty(e.id, qty - 1)}
                                        className="w-7 h-8 hover:bg-blue-50 text-[#1BA5D9]"
                                    >
                                        <Minus className="h-3 w-3 mx-auto" />
                                    </button>
                                    <input
                                        data-testid={`eq-qty-${e.id}`}
                                        type="number"
                                        min={0}
                                        value={qty}
                                        onChange={(ev) => setQty(e.id, ev.target.value)}
                                        className="w-10 h-8 text-center font-mono font-semibold border-0 outline-none"
                                    />
                                    <button
                                        data-testid={`eq-inc-${e.id}`}
                                        onClick={() => setQty(e.id, qty + 1)}
                                        className="w-7 h-8 hover:bg-blue-50 text-[#1BA5D9]"
                                    >
                                        <Plus className="h-3 w-3 mx-auto" />
                                    </button>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </Card>
    );
}

// =================================================================
// STEP 3 — Acquisition mode
// =================================================================
function Step3({ state, patch, patchDeep }) {
    const v = equiposSubtotal(state);
    const P = state.settings.params;

    return (
        <Card step={3} title="Modo de adquisición del equipo" subtitle="Cómo se factura la inversión">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
                {MODES.map(({ id, label, sub, icon: Icon }) => {
                    const on = state.mode === id;
                    return (
                        <button
                            key={id}
                            data-testid={`mode-${id}`}
                            aria-pressed={on}
                            onClick={() => patch({ mode: id })}
                            className={
                                "text-left rounded-md p-3 border transition-all duration-150 " +
                                (on
                                    ? "border-[#1BA5D9] bg-blue-50 ring-1 ring-[#1BA5D9]"
                                    : "border-slate-200 bg-white hover:border-slate-300")
                            }
                        >
                            <Icon className={"h-4 w-4 mb-1.5 " + (on ? "text-[#1BA5D9]" : "text-slate-500")} />
                            <div className="font-medium text-[13px] text-slate-900">{label}</div>
                            <div className="text-[11px] text-slate-500 leading-tight mt-0.5">{sub}</div>
                        </button>
                    );
                })}
            </div>

            <div className="mt-4">
                {state.mode === "contado" && (
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Descuento de contado">
                            <div className="relative">
                                <Input
                                    data-testid="contado-desc"
                                    type="number"
                                    min={0}
                                    max={40}
                                    value={state.contado.desc || 0}
                                    onChange={(e) => patchDeep("contado", { desc: +e.target.value || 0 })}
                                    className="h-9 rounded-md pr-8 font-mono focus-visible:ring-[#1BA5D9] border-slate-200"
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 font-mono">%</span>
                            </div>
                        </Field>
                        <div>
                            <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500 mb-1.5">
                                Inversión total
                            </div>
                            <div className="font-heading text-2xl font-semibold text-[#1BA5D9] font-mono" data-testid="contado-total">
                                {money0(v * (1 - (state.contado.desc || 0) / 100))}
                            </div>
                        </div>
                    </div>
                )}

                {state.mode === "financiamiento" && (
                    <FinanciamientoBlock state={state} patch={patch} patchDeep={patchDeep} v={v} P={P} />
                )}

                {state.mode === "alquiler" && (
                    <AlquilerBlock state={state} patch={patch} patchDeep={patchDeep} v={v} P={P} />
                )}

                {state.mode === "comodato" && <ComodatoBlock state={state} patch={patch} patchDeep={patchDeep} P={P} />}
            </div>
        </Card>
    );
}

function FinanciamientoBlock({ state, patchDeep, v, P }) {
    const eng = v * (state.fin.enganche / 100);
    const fin = v - eng;
    const cuota = PMT(fin, P.tasaFin, state.fin.plazo);
    return (
        <>
            <div className="grid sm:grid-cols-3 gap-3">
                <Field label="Enganche">
                    <div className="relative">
                        <Input
                            data-testid="fin-enganche"
                            type="number"
                            min={0}
                            max={80}
                            value={state.fin.enganche}
                            onChange={(e) => patchDeep("fin", { enganche: Math.min(80, Math.max(0, +e.target.value || 0)) })}
                            className="h-9 rounded-md pr-8 font-mono focus-visible:ring-[#1BA5D9] border-slate-200"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 font-mono">%</span>
                    </div>
                </Field>
                <Field label="Plazo">
                    <Select
                        value={String(state.fin.plazo)}
                        onValueChange={(v) => patchDeep("fin", { plazo: +v })}
                    >
                        <SelectTrigger data-testid="fin-plazo" className="h-9 rounded-md focus:ring-[#1BA5D9] border-slate-200">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="12">12 meses</SelectItem>
                            <SelectItem value="24">24 meses</SelectItem>
                            <SelectItem value="36">36 meses</SelectItem>
                            <SelectItem value="48">48 meses</SelectItem>
                        </SelectContent>
                    </Select>
                </Field>
                <Field label="Tasa anual">
                    <div className="relative">
                        <Input
                            data-testid="fin-tasa"
                            type="number"
                            step="0.5"
                            value={(P.tasaFin * 100).toFixed(1)}
                            onChange={(e) => {
                                patchDeep("settings", {
                                    params: { ...P, tasaFin: (+e.target.value || 0) / 100 },
                                });
                            }}
                            className="h-9 rounded-md pr-8 font-mono focus-visible:ring-[#1BA5D9] border-slate-200"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 font-mono">%</span>
                    </div>
                </Field>
            </div>
            <div className="grid sm:grid-cols-3 gap-3 mt-4 pt-4 border-t border-slate-100">
                <Kpi label="Enganche" value={money0(eng)} />
                <Kpi label="Cuota mensual" value={money(cuota)} accent />
                <Kpi label="Total financiado" value={money0(eng + cuota * state.fin.plazo)} />
            </div>
        </>
    );
}

function AlquilerBlock({ state, patchDeep, v, P }) {
    const meses = state.alq.anios * 12;
    const cuota = PMT(v, P.tasaAlq, meses);
    return (
        <>
            <div className="grid sm:grid-cols-3 gap-3">
                <Field label="Plazo del alquiler">
                    <Select
                        value={String(state.alq.anios)}
                        onValueChange={(v) => patchDeep("alq", { anios: +v })}
                    >
                        <SelectTrigger data-testid="alq-anios" className="h-9 rounded-md focus:ring-[#1BA5D9] border-slate-200">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="1">1 año</SelectItem>
                            <SelectItem value="2">2 años</SelectItem>
                        </SelectContent>
                    </Select>
                </Field>
                <Field label="Tasa anual">
                    <div className="relative">
                        <Input
                            data-testid="alq-tasa"
                            type="number"
                            step="0.5"
                            value={(P.tasaAlq * 100).toFixed(1)}
                            onChange={(e) =>
                                patchDeep("settings", {
                                    params: { ...P, tasaAlq: (+e.target.value || 0) / 100 },
                                })
                            }
                            className="h-9 rounded-md pr-8 font-mono focus-visible:ring-[#1BA5D9] border-slate-200"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 font-mono">%</span>
                    </div>
                </Field>
                <Kpi label="Canon mensual" value={money(cuota)} accent />
            </div>
            <div className="mt-3 text-[11.5px] text-slate-500">
                El equipo permanece como activo de MD Link; incluye mantenimiento preventivo. Renovable al término.
            </div>
        </>
    );
}

function ComodatoBlock({ state, patchDeep, P }) {
    const c = comodatoCalc(state);
    const tasaPct = ((state.com.tasa != null ? state.com.tasa : P.tasaFin) * 100).toFixed(1);
    return (
        <>
            <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Plazo del comodato">
                    <Select
                        value={String(state.com.anios || 1)}
                        onValueChange={(v) => patchDeep("com", { anios: +v })}
                    >
                        <SelectTrigger data-testid="com-anios" className="h-9 rounded-md focus:ring-[#1BA5D9] border-slate-200">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="1">1 año (base)</SelectItem>
                            <SelectItem value="2">1 año + prórroga 1 (2 años)</SelectItem>
                            <SelectItem value="3">1 año + prórroga 2 (3 años)</SelectItem>
                            <SelectItem value="4">1 año + prórroga 3 (4 años)</SelectItem>
                        </SelectContent>
                    </Select>
                </Field>
                <Field label="% de financiamiento (anual)">
                    <div className="relative">
                        <Input
                            data-testid="com-tasa"
                            type="number"
                            step="0.5"
                            value={tasaPct}
                            onChange={(e) => patchDeep("com", { tasa: (+e.target.value || 0) / 100 })}
                            className="h-9 rounded-md pr-8 font-mono focus-visible:ring-[#1BA5D9] border-slate-200"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 font-mono">%</span>
                    </div>
                </Field>
            </div>

            <div className="mt-4">
                <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500 mb-2">
                    Precio por consumible · por familia
                </div>
                {c.families.length ? (
                    <div className="space-y-1 border border-slate-200 rounded-md p-3 bg-slate-50/60">
                        {c.families.map((fa) => (
                            <div key={fa.fam} className="flex justify-between items-baseline py-1 border-b border-dashed border-slate-200 last:border-b-0">
                                <span className="text-[13px] font-medium text-slate-900">{fa.title}</span>
                                <span className="font-mono font-semibold text-[#1BA5D9] text-[13px]">
                                    {fa.flatUnit != null ? money(fa.flatUnit) + " / consumible" : "sin volumen"}
                                </span>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-[11.5px] text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-3">
                        Cargá los volúmenes en el paso 4 para calcular el precio por consumible de cada familia.
                    </div>
                )}
            </div>
            <div className="text-[11.5px] text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-3 mt-3 leading-relaxed">
                Cada equipo absorbe su propio costo: <b>precio por consumible = (equipo + {tasaPct}% financiamiento + consumibles de esa familia) ÷ unidades de la familia</b>. A mayor volumen o plazo, menor precio por unidad.
            </div>
        </>
    );
}

// =================================================================
// STEP 4 — Consumibles matrix
// =================================================================
function Step4({ state, setState, patch }) {
    const fams = selFamilias(state);
    const [openFam, setOpenFam] = useState(() => (fams[0] || null));

    // Ensure openFam is one of currently visible families
    const activeFam = fams.includes(openFam) ? openFam : fams[0] || null;

    const setVol = (id, v) => {
        const cons = { ...state.cons };
        const n = +v || 0;
        if (n > 0) cons[id] = n;
        else delete cons[id];
        setState({ ...state, cons });
    };

    const descPlazoTxt =
        state.mode === "comodato"
            ? "Definido por el plazo del comodato (paso 3)"
            : (plazoDiscount(state) * 100).toFixed(0) + "% adicional sobre unitario";

    return (
        <Card step={4} title="Matriz de consumibles" subtitle="Consumo anual, descuento por volumen y plazo">
            <div className="grid sm:grid-cols-2 gap-3 mb-2">
                <Field label="Plazo del contrato de consumibles">
                    <Select
                        value={state.plazoCons}
                        onValueChange={(v) => patch({ plazoCons: v })}
                        disabled={state.mode === "comodato"}
                    >
                        <SelectTrigger data-testid="plazo-cons" className="h-9 rounded-md focus:ring-[#1BA5D9] border-slate-200">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="1">1 año</SelectItem>
                            <SelectItem value="2">1 año + prórroga (2 años)</SelectItem>
                            <SelectItem value="3">2 años + prórroga (3 años)</SelectItem>
                            <SelectItem value="4">Multi-año (4 años)</SelectItem>
                        </SelectContent>
                    </Select>
                </Field>
                <Field label="Descuento por plazo">
                    <Input
                        data-testid="desc-plazo-info"
                        value={descPlazoTxt}
                        disabled
                        className="h-9 rounded-md font-mono text-[13px] border-slate-200 bg-slate-50"
                    />
                </Field>
            </div>

            {fams.length === 0 ? (
                <div className="text-[12px] text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-3 mt-2" data-testid="cons-empty-hint">
                    Seleccioná al menos un equipo para ver sus consumibles compatibles.
                </div>
            ) : (
                <div className="space-y-2">
                    {fams.map((fam) => {
                        const grp = CONSUMIBLES[fam];
                        if (!grp) return null;
                        const isOpen = activeFam === fam;
                        return (
                            <div
                                key={fam}
                                className="border border-slate-200 rounded-md overflow-hidden bg-white"
                                data-testid={`fam-${fam}`}
                            >
                                <button
                                    onClick={() => setOpenFam(isOpen ? null : fam)}
                                    className="w-full flex items-center gap-2 px-4 py-2.5 bg-slate-50/70 hover:bg-slate-100/70 transition-all duration-150"
                                    data-testid={`fam-toggle-${fam}`}
                                >
                                    <span className="font-medium text-[13px] text-slate-900">{grp.label}</span>
                                    <span className="text-[11px] text-slate-500 font-mono">· {grp.items.length} ref.</span>
                                    <ChevronRight className={"h-4 w-4 ml-auto text-slate-400 transition-transform duration-150 " + (isOpen ? "rotate-90" : "")} />
                                </button>
                                {isOpen && (
                                    <div className="px-2 pt-1 pb-2">
                                        {grp.items.map((cRaw) => {
                                            const c = consWithOverrides(state, cRaw);
                                            const vol = state.cons[c.id] || "";
                                            const voln = +vol || 0;
                                            const base = consUnitBase(state, c);
                                            const dv = volDiscount(state, c, voln);
                                            const dp = plazoDiscount(state);
                                            const u = base * (1 - dv) * (1 - dp);
                                            const totalDisc = Math.round((1 - (1 - dv) * (1 - dp)) * 100);
                                            return (
                                                <div
                                                    key={c.id}
                                                    className="grid grid-cols-12 gap-2 items-center py-2 border-b border-dashed border-slate-100 last:border-b-0 px-2"
                                                    data-testid={`cons-row-${c.id}`}
                                                >
                                                    <div className="col-span-12 sm:col-span-6 min-w-0">
                                                        <div className="text-[12.5px] font-medium text-slate-900 truncate">{c.nombre}</div>
                                                        <div className="text-[11px] text-slate-500 font-mono">
                                                            MOQ {N.format(c.moq)} u · ref. mínimo
                                                        </div>
                                                    </div>
                                                    <div className="col-span-7 sm:col-span-3">
                                                        <input
                                                            data-testid={`cons-vol-${c.id}`}
                                                            type="number"
                                                            min={0}
                                                            value={vol}
                                                            placeholder="0"
                                                            onChange={(e) => setVol(c.id, e.target.value)}
                                                            className="h-8 w-full border border-slate-200 rounded-md text-right px-2 font-mono focus-cobalt"
                                                        />
                                                        <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-0.5 text-right">
                                                            Consumo anual (u)
                                                        </div>
                                                    </div>
                                                    <div className="col-span-5 sm:col-span-3 text-right" data-testid={`cons-unit-${c.id}`}>
                                                        <div className="font-mono font-semibold text-[12.5px] text-slate-900">
                                                            {voln > 0 ? unit(u) : unit(base)}
                                                        </div>
                                                        <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-0.5">
                                                            {voln > 0 ? "precio unitario" : "base"}
                                                            {totalDisc > 0 && (
                                                                <span className="text-[#1BA5D9] font-mono ml-1">
                                                                    −{totalDisc}% vol/plazo
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </Card>
    );
}

// =================================================================
// Shared UI
// =================================================================
function Card({ step, title, subtitle, children }) {
    return (
        <section className="bg-white border border-slate-200 rounded-md overflow-hidden">
            <header className="flex items-center gap-3 px-5 py-3.5 border-b border-slate-200">
                <div className="h-7 w-7 rounded-sm bg-blue-50 text-[#1BA5D9] font-heading font-bold text-[13px] grid place-items-center flex-none">
                    {step}
                </div>
                <div className="min-w-0">
                    <h2 className="font-heading text-[15px] font-semibold tracking-tight text-slate-900 leading-tight">{title}</h2>
                    <div className="text-[11.5px] text-slate-500">{subtitle}</div>
                </div>
            </header>
            <div className="p-5">{children}</div>
        </section>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500 mb-1.5">{label}</div>
            {children}
        </label>
    );
}

function Kpi({ label, value, accent }) {
    return (
        <div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500 mb-1">{label}</div>
            <div className={"font-mono font-semibold text-[15px] " + (accent ? "text-[#1BA5D9]" : "text-slate-900")}>{value}</div>
        </div>
    );
}
